"use client";

import { useTranslations } from "next-intl";
import { ArrowSquareOut } from "@phosphor-icons/react/dist/ssr";
import AffiliateBadge from "@/components/affiliates/AffiliateBadge";
import AffiliateLogo from "@/components/affiliates/AffiliateLogo";
import { BADGE_TAB_CATEGORY } from "@/lib/badge-styles";
import { GRID_CARD_FOOTER, GRID_CARD_NAME_LEADING, GRID_CARD_TAB, gridCardNameSize } from "@/lib/card-styles";
import { getCategoryLabel } from "@/lib/categories";
import { AFFILIATE_REL, affiliateDomain, affiliateHref, type AffiliateLink } from "@/lib/affiliates";
import type { Locale } from "@/lib/locale";
import { AFFILIATE_EVENTS, track } from "@/lib/analytics";

/**
 * An affiliate link in the compact grid under articles and guides, built on
 * the same recipes as `ProfessionalCard compact` (`card-styles.ts`): insets,
 * 3:4 well, category tab, name leading and ruled footer. The footer carries
 * the "Affiliate link" tag where a pro card carries its location.
 */
export default function AffiliateGridCard({
  affiliate,
  locale = "en",
  activeCategories = [],
}: {
  affiliate: AffiliateLink;
  locale?: Locale;
  /** The page's categories: the tag shows the one the affiliate matched, not simply its first. */
  activeCategories?: readonly string[];
}) {
  const t = useTranslations("affiliates");
  const domain = affiliateDomain(affiliate.url);

  return (
    <article aria-label={`${affiliate.name}, ${t("label")}`} className="h-full">
      <a
        href={affiliateHref(affiliate.id, "article")}
        target="_blank"
        rel={AFFILIATE_REL}
        onClick={() => track(AFFILIATE_EVENTS.click, { id: affiliate.id, surface: "article" })}
        // Fills the grid cell and grows the block above the footer, so the
        // "Affiliate link" row lands on the card's bottom edge level with the
        // pro cards' meta rows beside it (owner, 2026-09-18).
        className="group flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2"
      >
        <div className="px-13 pt-13">
          <div className="relative">
            <AffiliateLogo
              name={affiliate.name}
              logo={affiliate.logo}
              sizes="(min-width: 1024px) 240px, (min-width: 640px) 33vw, 50vw"
              className="aspect-[3/4] w-full rounded-control"
              initialClass="text-5xl"
            />
            <span className={`${BADGE_TAB_CATEGORY} ${GRID_CARD_TAB.compact}`}>
              {getCategoryLabel(affiliate.categories.find((c) => activeCategories.includes(c)) ?? affiliate.categories[0], locale)}
            </span>
          </div>
        </div>

        <div className="flex flex-1 flex-col rounded-b-card bg-white p-13">
          <div className="flex-1">
          <h3 className="flex items-center gap-5 font-serif text-navy">
            {/* The pro card's no-JS size tiers, so the name does not out-shout the pro names beside it. */}
            <span className={`min-w-0 truncate ${GRID_CARD_NAME_LEADING.compact} ${gridCardNameSize(affiliate.name.length, true)}`}>
              {affiliate.name}
            </span>
            <ArrowSquareOut aria-hidden="true" weight="bold" className="h-13 w-13 shrink-0 text-navy/50" />
          </h3>
          <p className="mt-2 truncate text-xs text-gray-600">{affiliate.tagline}</p>
          </div>
          <footer className={GRID_CARD_FOOTER}>
            <AffiliateBadge plain />
          </footer>
        </div>
        <span className="sr-only">{t("opensExternal", { domain })}</span>
      </a>
    </article>
  );
}
